import React, { useEffect, useMemo, useState } from "react";
import { Badge, Modal } from "react-bootstrap";

import HttpManager from "../../../../services/HttpManager";
import PromocionDirecta, { faltaPromo, promoParaEnviar, promoVacia } from "../../../../components/acuerdos/promocionDirecta";
import { RequestPromotionDTO } from "../../../../models/EnrollmentHistoryDTO";
import { EnrollmentUpdateRow, MoneyMove, RepartoContext, TuitionSnapshot, UPDATE_TYPE, UpdateDetailsDTO } from "../../../../models/EnrollmentUpdateDTO";
import S from "./strings";
import { dinero, errorText, fechaCorta, lineaEnOro, tipoDe } from "./helpers";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { BotonWhatsappAlumno } from "../../../../components/whatsapp/whatsappAlumno";
import { etiquetaDe, etiquetaHorario } from "../../../../utils/etiquetaHorario";
import { useCapacidades } from "./apiBase";
import T from "./textosBase";
import { EleccionRecorrer, R as RT, RecorrerEleccion } from "./recorrerPagos";
import { RecorrerVistaDTO } from "./apiBase";
import { primeraMayuscula } from "../../../../utils/textoPropio";

/** Lo que no se pudo cargar: el motivo real del servidor y «Reintentar» (nunca un «Cargando…» sin fin). */
export const Fallo: React.FC<{ texto: string; onReintentar?: () => void; dentro?: boolean }> = ({ texto, onReintentar, dentro }) => (
	<div className="alert alert-warning d-flex justify-content-between align-items-center gap-2" role="alert" style={{ marginBottom: dentro ? 0 : 12, fontSize: 14 }}>
		<span>{texto}</span>
		{onReintentar ? <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onReintentar}>{T.retry}</button> : null}
	</div>
);

/** Small modal asking for one text (Qué corregir, motivo, comentario). Empty text keeps the button off. */
export const PromptModal: React.FC<{ title: string; label: string; confirm: string; initial?: string; optional?: boolean; busy?: boolean;
	onCancel: () => void; onConfirm: (text: string) => void; children?: React.ReactNode }> = ({ title, label, confirm, initial, optional, busy, onCancel, onConfirm, children }) => {
	const [text, setText] = useState<string>(initial || "");
	return (
		<Modal show onHide={() => { if (!busy) { onCancel(); } }} centered>
			<Modal.Header closeButton><Modal.Title as="h5">{title}</Modal.Title></Modal.Header>
			<Modal.Body>
				{children}
				<label className="wau-campo" style={{ width: "100%" }}><span className="wau-lab">{label}</span>
					<textarea className="wau-entrada" rows={3} value={text} onChange={(e) => setText(e.target.value)} autoFocus /></label>
			</Modal.Body>
			<Modal.Footer>
				<button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel} disabled={busy}>{S.cancel}</button>
				<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy || (!optional && text.trim().length < 3)} onClick={() => onConfirm(text.trim())}>{confirm}</button>
			</Modal.Footer>
		</Modal>
	);
};

export interface PersonaConfirmar { nombre: string; curso: string; tipo: string; lineas: string[]; }

/**
 * Confirmación antes de todo Aplicar (Actualizaciones Claras, 2026-10-31): quién es, qué cambio y «Qué Va a Pasar», por persona.
 * Con una persona el título es «Aplicar a Nombre»; con varias, «Aplicar N».
 */
export const ConfirmarAplicar: React.FC<{ personas: PersonaConfirmar[]; busy?: boolean; onCancel: () => void; onConfirm: () => void }> = ({ personas, busy, onCancel, onConfirm }) => (
	<Modal show onHide={() => { if (!busy) { onCancel(); } }} centered>
		<Modal.Header closeButton><Modal.Title as="h5">{personas.length === 1 ? S.confirmTitle(personas[0].nombre) : S.confirmTitleN(personas.length)}</Modal.Title></Modal.Header>
		<Modal.Body>
			<div className="wau-confirma">
				{personas.map((x, i) => (
					<div className="persona" key={"p" + i}>
						<div className="d-flex flex-wrap align-items-center gap-1">
							{personas.length > 1 ? <b style={{ marginRight: 4 }}>{x.nombre}</b> : null}
							{x.curso ? <Badge bg="secondary" style={{ fontWeight: 500 }}>{x.curso}</Badge> : null}
							{x.tipo ? <Badge bg="info" style={{ fontWeight: 500 }}>{x.tipo}</Badge> : null}
						</div>
						<div className="wau-efectos">{x.lineas.map((l, j) => <div key={"l" + j} className={lineaEnOro(l) ? "wau-oro" : ""}>{l}</div>)}</div>
					</div>
				))}
			</div>
		</Modal.Body>
		<Modal.Footer>
			<button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel} disabled={busy}>{S.confirmBack}</button>
			<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy} onClick={onConfirm}>{busy ? S.loading : S.confirmApply}</button>
		</Modal.Footer>
	</Modal>
);

/** El reparto de dinero que dejó una actualización aplicada (Cambio a otro curso o Retiro), si movió algo. */
export const auditoriaReparto = (r: EnrollmentUpdateRow): number | null => {
	const rep = r.result?.reparto;
	return r.statusId === 3 && rep && Number(rep.auditItemId) > 0 && Number(rep.moved) > 0 ? Number(rep.auditItemId) : null;
};

/** ¿Quien mira es Caja? (el servidor solo deja deshacer un reparto a Caja). Sale de las capacidades, no de `_options`. */
export const useEsCaja = (campusId: number): boolean => useCapacidades(campusId).isCaja;

/** Deshacer Reparto: pide el motivo y devuelve el dinero a sus mensualidades de origen. El motivo real del servidor se muestra tal cual. */
export const DeshacerReparto: React.FC<{ campusId: number; auditItemId: number; studentName: string; onDone: (msg: string) => void; onError: (msg: string) => void; countryId?: number | null }> = ({ campusId, auditItemId, studentName, onDone, onError, countryId }) => {
	const [abierto, setAbierto] = useState<boolean>(false);
	const [busy, setBusy] = useState<boolean>(false);
	return (
		<>
			<button type="button" className="wau-btn s mini" onClick={() => setAbierto(true)}>{S.undoReparto}</button>
			{abierto ? (
				<PromptModal title={S.undoReparto} label={S.undoRepartoReason} confirm={S.undoReparto} busy={busy} onCancel={() => setAbierto(false)}
					onConfirm={(nota) => {
						setBusy(true);
						HttpManager.getInstance().enrollmentUpdatesService.undoReparto(campusId, auditItemId, nota)
							.then((r) => { setAbierto(false); onDone(S.undoRepartoDone(dinero(r.data.amount, countryId))); })
							.catch((e) => { setAbierto(false); onError(errorText(e)); })
							.finally(() => setBusy(false));
					}}>
					<p className="wau-sec" style={{ marginTop: 0 }}>{studentName}</p>
				</PromptModal>
			) : null}
		</>
	);
};

/** Payment plan as month chips: paid, overdue, No cursada, frozen. Only monthly fees (typeId 1). */
export const PlanMeses: React.FC<{ tuitions: TuitionSnapshot[] }> = ({ tuitions }) => (
	<div className="wau-meses wau-num">
		{tuitions.filter((t) => t.typeId === 1).map((t) => {
			const cls = t.progressStatusId === 3 ? "nocursada" : t.statusId === 2 ? "pagada" : t.statusId === 3 ? "vencida" : t.frozen ? "congelada" : "";
			return <span key={"m_" + t.id} className={"wau-mes " + cls} title={t.title}><b>{t.tuitionNumber}</b>{fechaCorta(t.dueDate)}</span>;
		})}
	</div>
);

/*
	Lo pagado que no se cursará. `reparto.pool` solo cuenta mensualidades que ya están No cursadas, y antes de aplicar
	un Cambio a otro curso o un Retiro todavía no lo están: se suma lo pagado de las mensualidades por venir.
*/
export const estimatePool = (d: UpdateDetailsDTO): number => {
	const pool = d.reparto?.pool?.total || 0;
	if (pool > 0) { return pool; }
	if (d.reparto?.poolProjected) { return d.reparto.poolProjected.total || 0; }
	if (d.previewNow?.paidNotCoursedAfter) { return d.previewNow.paidNotCoursedAfter; }
	if (d.typeId !== UPDATE_TYPE.COURSE_CHANGE || !d.enrollment) { return 0; }
	const hoy = new Date().toISOString().slice(0, 10);
	return Math.round(d.enrollment.tuitions
		.filter((t) => t.typeId === 1 && t.progressStatusId !== 2 && (t.startDate || t.dueDate || "") > hoy)
		.reduce((acc, t) => acc + (Number(t.paidAmount) || 0), 0) * 100) / 100;
};

interface RepartoLine { key: string; label: string; sub: string; move: Omit<MoneyMove, "amount">; }

/** Editable split of what was paid and won't be coursed. The rest stays as "Pagado no cursado" (never saldo a favor). */
export const RepartoEditor: React.FC<{ reparto: RepartoContext; pool: number; countryId?: number | null; initial?: MoneyMove[] | null;
	onChange: (moves: MoneyMove[], over: boolean) => void }> = ({ reparto, pool, countryId, initial, onChange }) => {
	const lines: RepartoLine[] = useMemo(() => {
		const out: RepartoLine[] = [];
		(reparto.newPlan?.tuitionNumbers || []).forEach((n) => out.push({ key: "n" + n, label: S.newPlanTuition(n - 1), sub: dinero(reparto.newPlan?.monthlyPrice, countryId), move: { target: "tuition", tuitionNumber: n } }));
		(reparto.otherTuitions || []).forEach((t) => out.push({ key: "t" + t.id, label: `${t.title}${t.courseName ? ", " + t.courseName : ""}`, sub: S.pendingAmount(dinero(t.pending, countryId)), move: { target: "tuition", id: t.id } }));
		(reparto.articles || []).forEach((a) => out.push({ key: "a" + a.id, label: a.title, sub: S.pendingAmount(dinero(a.pending, countryId)), move: { target: "article", id: a.id } }));
		return out;
	}, [reparto, countryId]);
	const [values, setValues] = useState<{ [k: string]: string }>(() => {
		const v: { [k: string]: string } = {};
		(initial || []).forEach((m) => {
			const l = lines.find((x) => x.move.target === m.target && (m.tuitionNumber ? x.move.tuitionNumber === m.tuitionNumber : x.move.id === m.id));
			if (l) { v[l.key] = String(m.amount); }
		});
		return v;
	});
	const total = lines.reduce((acc, l) => acc + (Number(values[l.key]) || 0), 0);
	const rest = Math.round((pool - total) * 100) / 100;
	useEffect(() => {
		onChange(lines.filter((l) => Number(values[l.key]) > 0).map((l) => ({ ...l.move, amount: Number(values[l.key]) })), rest < 0);
	}, [values]);
	return (
		<div className="wau-reparto">
			{lines.map((l) => (
				<div className="wau-rep" key={l.key}>
					<span>{l.label}<span className="sub">{l.sub}</span></span>
					<input className="wau-entrada wau-num" inputMode="decimal" aria-label={l.label} value={values[l.key] ?? ""} placeholder="0"
						onChange={(e) => setValues({ ...values, [l.key]: e.target.value.replace(/[^0-9.]/g, "") })} />
				</div>
			))}
			<div className="wau-rep resto"><span>{S.remainder}</span><span className="wau-num">{dinero(Math.max(0, rest), countryId)}</span></div>
			{rest < 0 ? <p className="wau-oro" style={{ fontSize: 13.5, margin: 0 }}>{S.overAssigned}</p> : ""}
		</div>
	);
};

const fromTo = (d: UpdateDetailsDTO): string => {
	const from = (d.fromSchedule ? etiquetaDe(d.fromSchedule) : etiquetaHorario(d.enrollment?.scheduleName, d.enrollment?.periodName)) || "—";
	const to = d.toCourse ? `${d.toCourse.name}${d.toSchedule ? ", " + etiquetaDe(d.toSchedule) : ""}` : etiquetaDe(d.toSchedule);
	return to ? `${from} → ${to}` : from;
};

/**
 * One update, ready to apply or return: plan, avance, warnings, sesiones de más (3 opciones SIN default),
 * reparto, WhatsApp and the buttons. Shared by "Por aplicar" and "Cambio a otro curso".
 */
export const UpdateDetail: React.FC<{ campusId: number; countryId?: number | null; id: number; onChanged: (msg: string) => void; notifyError: (msg: string) => void }> = ({ campusId, countryId, id, onChanged, notifyError }) => {
	const http = HttpManager.getInstance();
	const [d, setD] = useState<UpdateDetailsDTO | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [extra, setExtra] = useState<number | null>(null);
	const [extraMonths, setExtraMonths] = useState<string>("");
	const [extraPrice, setExtraPrice] = useState<string>("");
	const [lastSession, setLastSession] = useState<string>("");
	const [money, setMoney] = useState<MoneyMove[]>([]);
	const [over, setOver] = useState<boolean>(false);
	const [prompt, setPrompt] = useState<"return" | "discard" | "resubmit" | null>(null);
	const [promo, setPromo] = useState<RequestPromotionDTO>(promoVacia());
	const [fallo, setFallo] = useState<string | null>(null);
	const [intento, setIntento] = useState<number>(0);
	const [confirmando, setConfirmando] = useState<boolean>(false);
	// Fase 2: Recorrer Pagos (marcado si se atrasa) y «Ya Tomó Esas Clases» (no quedan por reponer)
	const [rec, setRec] = useState<EleccionRecorrer>({ on: false, meses: 0 });
	const [vistaRec, setVistaRec] = useState<RecorrerVistaDTO | null>(null);
	const [yaTomadas, setYaTomadas] = useState<boolean>(false);

	useEffect(() => {
		let vigente = true;
		setD(null); setExtra(null); setMoney([]); setOver(false); setPrompt(null); setFallo(null); setYaTomadas(false); setVistaRec(null);
		http.enrollmentUpdatesService.details(campusId, id).then((r) => {
			if (!vigente) { return; }
			setD(r.data);
			setPromo(r.data.options?.promotion?.items ? r.data.options.promotion : promoVacia());
			const sug = r.data.previewNow?.recorrer?.sugerido ?? 0;
			const elegido = r.data.options?.recorrer;
			setRec(elegido !== undefined && elegido !== null ? { on: Number(elegido) !== 0, meses: Number(elegido) || sug } : { on: sug > 0, meses: sug });
			setYaTomadas(!!r.data.options?.yaTomadas);
			// Con pagos por recorrer y clases que repite, lo normal es recorrer (decisión 1). Un cierre / fusión ya guardó «recorrer» con la
			// opción 1: se respeta como Recorrer (revisión 1-nov: Caja lo perdía al aplicar).
			const repite = (r.data.previewNow?.sessionsMore || 0) > 0;
			const op = r.data.extraSessionsOption;
			setExtra(repite && sug > 0 && (op === null || op === undefined || op === 1) && (elegido === undefined || elegido === null || Number(elegido) > 0) ? 4 : (op ?? null));
			setExtraMonths(String(r.data.extraMonths ?? r.data.previewNow?.extraMonthsSuggested ?? ""));
			setExtraPrice(r.data.extraPrice != null ? String(r.data.extraPrice) : "");
			setLastSession(r.data.lastSessionTaken != null ? String(r.data.lastSessionTaken) : "");
		}).catch((e) => vigente && setFallo(errorText(e)));
		return () => { vigente = false; };
	}, [campusId, id, intento]);

	if (fallo) { return <Fallo texto={fallo} onReintentar={() => setIntento((n) => n + 1)} />; }
	if (!d) { return <div className="wau-caja wau-sec">{S.loading}</div>; }

	const p = d.previewNow || d.preview;
	const open = d.statusId === 1 || d.statusId === 2;
	const more = p?.sessionsMore || 0;
	const typeLabel = tipoDe(d);
	const pool = estimatePool(d);
	// Retiro: el reparto se ve siempre, porque la última sesión que escribe Caja aquí puede dejar meses pagados sin cursar (revisión 31-oct).
	const showReparto = open && !!d.reparto && (d.typeId === UPDATE_TYPE.COURSE_CHANGE || d.typeId === UPDATE_TYPE.RETIRED || pool > 0);
	const isRetiro = d.typeId === UPDATE_TYPE.RETIRED;
	const needsExtra = open && more > 0;
	const esCurso = d.typeId === UPDATE_TYPE.COURSE_CHANGE;
	const faltaPromocion = open && esCurso && (!d.toSchedule || faltaPromo(promo, [d.toSchedule.id]));
	const sug = open && d.typeId === UPDATE_TYPE.SCHEDULE_CHANGE ? (p?.recorrer?.sugerido ?? 0) : 0;
	const recorreConRepetidas = needsExtra && sug > 0;   // las 3 opciones: Recorrer / Cobrar el mes / Descuento
	const recorrerOn = recorreConRepetidas ? extra === 4 : rec.on;
	const recorrerMal = recorrerOn && (!!vistaRec?.problemas?.length || rec.meses === 0);
	const less = p?.sessionsLess || 0;
	// Por qué no se puede aplicar todavía (revisión 1-nov: el botón quedaba apagado sin decir nada)
	const falta: string[] = [];
	if (open && d.canApply) {
		if (needsExtra && extra === null) { falta.push(S.chooseExtra); }
		if (faltaPromocion) { falta.push("Falta elegir la promoción del curso nuevo."); }
		if (over) { falta.push(S.overAssigned); }
		if (recorrerMal) { falta.push(vistaRec?.problemas?.[0] || "Elige cuántos meses recorrer."); }
	}
	const canApply = open && d.canApply && falta.length === 0;

	const run = (fn: () => Promise<any>, msg: string) => {
		setBusy(true);
		fn().then(() => { setPrompt(null); onChanged(msg); }).catch((e) => notifyError(errorText(e))).finally(() => setBusy(false));
	};
	const apply = () => { setConfirmando(false); run(() => http.enrollmentUpdatesService.apply(campusId, d.id, {
		extraSessionsOption: needsExtra ? extra : null,
		recorrer: sug !== 0 ? (recorrerOn ? rec.meses : 0) : undefined,
		yaTomadas: less > 0 ? yaTomadas : undefined,
		extraMonths: extra === 2 ? Number(extraMonths) || null : null,
		extraPrice: extra === 3 ? Number(extraPrice) || null : null,
		money: showReparto ? money : undefined,
		lastSessionTaken: isRetiro && lastSession ? Number(lastSession) : undefined,
		promotion: esCurso && d.toSchedule ? promoParaEnviar(promo, d.toSchedule.id, {}, true) : undefined
	}), S.applied); };
	// Lo que se confirma: «Qué Va a Pasar» del servidor + las decisiones tomadas aquí (clases que repite, reparto).
	const lineasConfirmar = (): string[] => {
		// Con el reparto ya decidido aquí, «Caja decide…» sobra: se dice lo que Caja eligió.
		// Lo que Caja ya decidió aquí reemplaza a «Caja decide/elige…» y a lo sugerido (revisión 1-nov: salían los dos).
		const out = (p?.efectos?.length ? p.efectos : (p?.warnings || [])).filter((l) => !((showReparto && /Caja (decide|lo reparte)/.test(l))
			|| (needsExtra && extra !== null && /Caja elige|decide Caja/.test(l)) || (sug !== 0 && /^(Sugerido:|Próximo pago|Va adelante|Sus mensualidades se quedan|Mensualidades: mismos meses|Recorre |Adelanta |Termina de pagar|Total por pagar)/.test(l))
			|| (less > 0 && yaTomadas && /por reponer/.test(l))));
		if (needsExtra && extra === 4) { out.push(`${S.repiteClases(more)}, cubiertas al recorrer sus pagos.`); }
		if (needsExtra && extra !== null && extra !== 4) { const o = S.extraOptions.find((x) => x.id === extra); if (o) { out.push(`${S.repiteClases(more)}: ${o.t.toLowerCase()}${extra === 2 && extraMonths ? `, ${extraMonths}` : ""}${extra === 3 && extraPrice ? `, ${dinero(extraPrice, countryId)}` : ""}.`); } }
		if (sug !== 0) { if (recorrerOn && vistaRec) { out.push(...vistaRec.lineas); } else if (!recorrerOn) { out.push("Mensualidades: mismos meses, nuevo día de pago."); } }
		if (less > 0 && yaTomadas) { out.push("Ya tomó esas clases: no quedan por reponer."); }
		if (showReparto) { const movido = money.reduce((acc, m) => acc + (Number(m.amount) || 0), 0); out.push(S.repartoResumen(money.filter((m) => Number(m.amount) > 0).length, dinero(Math.max(0, pool - movido), countryId))); }
		return out;
	};

	const sessionsTotal = d.enrollment?.sessionsTotal || 0;
	return (
		<div>
			<div className="wau-caja">
				<div className="wau-fila" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
					<div className="wau-campo" style={{ gap: 4 }}>
						<div className="d-flex flex-wrap align-items-center gap-1">
							<b style={{ fontSize: 17, marginRight: 4 }}>{d.studentName}</b>
							{d.courseName ? <Badge bg="secondary" style={{ fontWeight: 500 }}>{cursoCorto(d.courseName) || d.courseName}</Badge> : null}
							<Badge bg="info" style={{ fontWeight: 500 }}>{typeLabel}</Badge>
						</div>
						<span className="wau-sec small">{d.requestedBy || "—"}, {fechaCorta(d.requestedAt)}</span>
					</div>
					<span className="wau-estado">{fromTo(d)}</span>
				</div>
				{d.enrollment ? (
					<div className="wau-dos">
						<div className="wau-campo"><span className="wau-lab">{S.plan}{d.enrollment.paidThrough ? ", " + d.enrollment.paidThrough : ""}{d.enrollment.overdue > 0 ? ", " : ""}{d.enrollment.overdue > 0 ? <span className="wau-oro">{S.overdue} {dinero(d.enrollment.overdue, countryId)}</span> : ""}</span>
							<PlanMeses tuitions={d.enrollment.tuitions} /></div>
						<div className="wau-campo"><span className="wau-lab">{S.progress(d.enrollment.sessionsTaken, sessionsTotal)}{d.enrollment.sessionsToRecover > 0 ? ", " : ""}{d.enrollment.sessionsToRecover > 0 ? <span className="wau-oro">{S.lessSessions(d.enrollment.sessionsToRecover)}</span> : ""}</span>
							{p?.target ? <span className="wau-sec" style={{ fontSize: 13.5 }}>{etiquetaHorario(p.target.scheduleName, p.target.periodName)}, {S.goesInSession(p.target.classesGiven + 1)}</span> : ""}</div>
					</div>
				) : ""}
				{open && (p?.efectos?.length || (p?.warnings || []).length > 0) ? (() => {
					const lineas = p?.efectos?.length ? p.efectos : (p?.warnings || []);
					return (
						<div className={lineas.some(lineaEnOro) ? "waAviso" : "wau-campo"} style={{ marginTop: 10 }}><span className="wau-lab">{S.col.whatHappens}</span>
							<div className="wau-efectos" style={{ fontSize: 14.5 }}>{lineas.map((w, i) => <div key={"w" + i} className={lineaEnOro(w) ? "wau-oro" : ""}>{w}</div>)}</div></div>
					);
				})() : ""}
				<div className="wau-hilo">
					<div><span className="cuando">{d.requestedBy}</span><span>"{primeraMayuscula(d.noteOwn || d.note)}"</span></div>
					{d.comments.map((c) => <div key={"c" + c.id}><span className="cuando">{c.author}, {fechaCorta(c.createdAt)}</span><span>{c.text}</span></div>)}
					{d.returnReason ? <div className="wau-oro"><span className="cuando">{S.fixReason}</span>{d.returnReason}</div> : ""}
				</div>
			</div>
			{open && esCurso && d.previousAgreement && !(p?.warnings || []).includes(d.previousAgreement.text) ? <div className="wau-nota" style={{ marginBottom: 12 }}><span>{d.previousAgreement.text}</span></div> : ""}
			{open && esCurso && d.toSchedule ? (
				<div className="wau-caja"><PromocionDirecta campusId={campusId} studentId={d.studentId} studentName={d.studentName} scheduleIds={[d.toSchedule.id]} value={promo} onChange={setPromo} busy={busy} marcar={false} /></div>
			) : ""}
			{needsExtra ? (
				<div className="wau-caja"><h3>{S.extraTitle(more)}</h3>
					<div className="wau-opciones" role="radiogroup">
						{recorreConRepetidas ? (
							<label className={"wau-opcion" + (extra === 4 ? " on" : "")}>
								<input type="radio" name={"extra_" + d.id} checked={extra === 4} onChange={() => { setExtra(4); setRec({ on: true, meses: rec.meses || sug }); }} />
								<span className="t">{RT.recorrer(rec.meses || sug)}</span><span className="d">Sus pagos caen con su grupo nuevo; las clases que repite no se cobran.</span>
							</label>
						) : null}
						{S.extraOptions.filter((o) => !(recorreConRepetidas && o.id === 1)).map((o) => (
							<label key={"x" + o.id} className={"wau-opcion" + (extra === o.id ? " on" : "")}>
								<input type="radio" name={"extra_" + d.id} checked={extra === o.id} onChange={() => { setExtra(o.id); setRec({ ...rec, on: false }); }} />
								<span className="t">{recorreConRepetidas && o.id === 2 ? "Cobrar el Mes que Repite" : o.t}</span><span className="d">{o.d}</span>
							</label>
						))}
					</div>
					{recorreConRepetidas && extra === 4 && d.enrollmentId ? (
						<RecorrerEleccion soloDetalle campusId={campusId} enrollmentId={d.enrollmentId} toScheduleId={d.toSchedule?.id ?? null} sugerido={sug} valor={{ on: true, meses: rec.meses || sug }}
							onCambio={(v) => setRec(v)} onVista={setVistaRec} disabled={busy} />
					) : null}
					{extra === 2 ? <div className="wau-campo" style={{ marginTop: 10, maxWidth: 160 }}><span className="wau-lab">{S.extraMonths}</span>
						<input className="wau-entrada wau-num" inputMode="numeric" value={extraMonths} onChange={(e) => setExtraMonths(e.target.value.replace(/\D/g, ""))} /></div> : ""}
					{extra === 3 ? <div className="wau-campo" style={{ marginTop: 10, maxWidth: 160 }}><span className="wau-lab">{S.extraPrice}</span>
						<input className="wau-entrada wau-num" inputMode="decimal" value={extraPrice} onChange={(e) => setExtraPrice(e.target.value.replace(/[^0-9.]/g, ""))} /></div> : ""}
				</div>
			) : ""}
			{open && sug !== 0 && !recorreConRepetidas && d.enrollmentId ? (
				<div className="wau-caja"><h3>Pagos</h3>
					<RecorrerEleccion campusId={campusId} enrollmentId={d.enrollmentId} toScheduleId={d.toSchedule?.id ?? null} sugerido={sug} valor={rec} onCambio={setRec} onVista={setVistaRec} disabled={busy} />
				</div>
			) : ""}
			{open && less > 0 ? (
				<div className="wau-caja" style={{ paddingTop: 10, paddingBottom: 10 }}>
					<label className={"wau-opcion" + (yaTomadas ? " on" : "")} style={{ marginBottom: 0 }}>
						<input type="checkbox" checked={yaTomadas} onChange={(e) => setYaTomadas(e.target.checked)} disabled={busy} />
						<span className="t">{RT.yaTomadas}</span><span className="d">{less} {less === 1 ? "clase no queda" : "clases no quedan"} por reponer.</span>
					</label>
				</div>
			) : ""}
			{open && isRetiro ? (
				<div className="wau-caja"><div className="wau-campo" style={{ maxWidth: 220 }}><span className="wau-lab">{S.lastSession}</span>
					<input className="wau-entrada wau-num" inputMode="numeric" value={lastSession} onChange={(e) => setLastSession(e.target.value.replace(/\D/g, ""))} /></div></div>
			) : ""}
			{showReparto && d.reparto ? (
				<div className="wau-caja"><h3>{S.reparto}</h3>
					<p className="wau-sec" style={{ fontSize: 13.5 }}>{S.paidNotCoursedLine(dinero(pool, countryId))}</p>
					<RepartoEditor reparto={d.reparto} pool={pool} countryId={countryId} initial={d.money} onChange={(m, o) => { setMoney(m); setOver(o); }} />
				</div>
			) : ""}
			{!open && d.whatsappText ? (
				<div className="wau-wa">{d.whatsappText}<div><BotonWhatsappAlumno campusId={campusId} studentId={d.studentId} nombre={d.studentName} texto sugerido={d.whatsappText} /></div></div>
			) : ""}
			{open ? (
				<div className="wau-acciones">
					{d.statusId === 2 ? <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setPrompt("resubmit")}>{S.resubmit}</button> : ""}
					<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setPrompt("discard")}>{S.reject}</button>
					{d.canReturn && d.statusId === 1 ? <button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setPrompt("return")}>{S.askFix}</button> : ""}
					{d.canApply ? <button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy || !canApply} onClick={() => setConfirmando(true)}>{S.apply}</button>
						: <span className="wau-sec" style={{ fontSize: 13.5 }}>{S.onlyCaja}</span>}
				</div>
			) : ""}
			{falta.length > 0 ? <div className="wau-oro" style={{ fontSize: 13.5, textAlign: "right", marginTop: 6 }}>{falta.map((f, i) => <div key={"f" + i}>{f}</div>)}</div> : ""}
			{confirmando ? <ConfirmarAplicar personas={[{ nombre: d.studentName, curso: cursoCorto(d.courseName) || d.courseName || "", tipo: typeLabel, lineas: lineasConfirmar() }]} busy={busy} onCancel={() => setConfirmando(false)} onConfirm={apply} /> : ""}
			{prompt === "return" ? <PromptModal title={S.askFix} label={S.fixReason} confirm={S.askFix} busy={busy} onCancel={() => setPrompt(null)}
				onConfirm={(t) => run(() => http.enrollmentUpdatesService.returnUpdate(campusId, d.id, t), S.fixed)} /> : ""}
			{prompt === "discard" ? <PromptModal title={S.reject} label={S.rejectReason} confirm={S.reject} busy={busy} onCancel={() => setPrompt(null)}
				onConfirm={(t) => run(() => http.enrollmentUpdatesService.discard(campusId, d.id, t), S.rejected)} /> : ""}
			{prompt === "resubmit" ? <PromptModal title={S.resubmit} label={S.resubmitComment} confirm={S.resubmit} optional busy={busy} onCancel={() => setPrompt(null)}
				onConfirm={(t) => run(() => http.enrollmentUpdatesService.resubmit(campusId, d.id, { comment: t || undefined }), S.resubmitted)} /> : ""}
		</div>
	);
};
